import React from 'react'
import { Box, Typography, Skeleton, useTheme } from '@mui/material'
import type { InterestScore } from 'api/league'
import { getScoreColor } from 'utils/scoreColor'
import MetricRow from 'components/structure/MetricRow'
import InterestScoreBadge from 'components/structure/InterestScoreBadge'

interface Props {
  interest: InterestScore | null
  loading?: boolean
}

const InterestScoreCard: React.FC<Props> = ({ interest, loading }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25, gap: 1 }}>
          <Skeleton variant="text" width={120} height={28} />
          <Skeleton variant="rounded" width={96} height={30} sx={{ borderRadius: 1.25 }} />
        </Box>
        <Skeleton variant="rounded" height={12} sx={{ mb: 1.2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={12} sx={{ mb: 1.2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={12} sx={{ mb: 1.5, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1.5 }} />
      </Box>
    )
  }

  if (!interest) {
    return (
      <Typography
        sx={{
          fontFamily: 'PremierLeagueRegular',
          fontSize: '0.8rem',
          opacity: 0.65,
          lineHeight: 1.5,
        }}
      >
        No interest score yet. Hit Calculate again to refresh (cached results may be outdated).
      </Typography>
    )
  }

  const scoreColor = getScoreColor(interest.total_interest_score)

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.25,
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'PremierLeagueBold',
            fontSize: { xs: '1rem', sm: '1.25rem' },
            lineHeight: 1.2,
          }}
        >
          Interest Score
        </Typography>
        <InterestScoreBadge score={interest.total_interest_score} verdict={interest.verdict} />
      </Box>

      <MetricRow
        label="Captain Diversity"
        value={interest.captain_diversity}
        color={getScoreColor(interest.captain_diversity * 10)}
      />
      <MetricRow
        label="Differentials"
        value={interest.differentials_captains}
        color={getScoreColor(interest.differentials_captains * 10)}
      />
      <MetricRow
        label="Chip Activity"
        value={interest.chips_usability}
        color={getScoreColor(interest.chips_usability * 10)}
      />
      <MetricRow
        label="Transfer Activity"
        value={interest.transfer_activity ?? 0}
        color={getScoreColor((interest.transfer_activity ?? 0) * 10)}
      />

      <Box
        sx={{
          mt: 1.25,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.035)',
          borderLeft: `2.5px solid ${scoreColor}`,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'PremierLeagueRegular',
            fontSize: '0.78rem',
            lineHeight: 1.55,
            fontStyle: 'italic',
            opacity: 0.78,
          }}
        >
          {interest.summary}
        </Typography>
      </Box>
    </Box>
  )
}

export default InterestScoreCard
